import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ReverseInfiniteScrollDirective } from './directives/reverse-infinite-scroll';
import { SafePipe } from './pipes/safe.pipe';
import { MaintenanceComponent } from './pages/maintenance/maintenance.component';
import { LinkifyPipe } from './pipes/linkify.pipe';
import { NgVar } from './directives/ng-var/ng-var.directive';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { DisplayNamePipe } from './pipes/display-name.pipe';
import { ImageFallbackPipe } from './pipes/image-fallback.pipe';
import { AddDaysPipe } from './pipes/add-days.pipe';
import { TileSelectInputComponent } from './components/inputs/tile-select-input/tile-select-input.component';
import {
    MAT_SNACK_BAR_DEFAULT_OPTIONS,
    MatSnackBarModule,
} from '@angular/material/snack-bar';
import { SuccessSnackBarComponent } from './components/snack-bars/success-snack-bar/success-snack-bar.component';
import { InfoSnackBarComponent } from './components/snack-bars/info-snack-bar/info-snack-bar.component';
import { FailureSnackBarComponent } from './components/snack-bars/failure-snack-bar/failure-snack-bar.component';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { TagSelectInputComponent } from './components/inputs/tag-select-input/tag-select-input.component';
import { SelectOwnerDialogComponent } from './components/select-owner-dialog/select-owner-dialog.component';
import { MatSelectModule } from '@angular/material/select';
import { MatListModule } from '@angular/material/list';
import { FieldInputDialogComponent } from './services/input-dialog/field-input-dialog/field-input-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
    DateAdapter,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
    MatNativeDateModule, MatRippleModule,
} from '@angular/material/core';
import { BmiPipe } from './pipes/bmi.pipe';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import { IncomingCallComponent } from './components/snack-bars/incoming-call/incoming-call.component';
import { PrescribePatientDialogComponent } from './components/prescribe-patient-dialog/prescribe-patient-dialog.component';
import { ProgramSelectInputComponent } from './components/inputs/program-select-input/program-select-input.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DietChartCardComponent } from './components/diet-chart-card/diet-chart-card.component';
import { QuillModule } from "ngx-quill";
import { MatQuill } from "./components/inputs/mat-quill/mat-quill";
import { FileDragAndDropDirective } from './directives/file-drag-and-drop/file-drag-and-drop.directive';
import { TdeePipe } from './pipes/tdee.pipe';
import { DayBasedDietChartEditorComponent } from './components/diet-chart-editor/day-based-diet-chart-editor/day-based-diet-chart-editor.component';
import { MatMenuModule } from "@angular/material/menu";
import { MatTooltipModule } from "@angular/material/tooltip";
import { FoodMeasureNamePipe, MealCardComponent } from "./components/diet-chart-editor/meal-card/meal-card.component";
import {
    EditMealDialogComponent
} from "./components/diet-chart-editor/edit-meal-dialog/edit-meal-dialog.component";
import {
    AddFoodItemMenuComponent
} from "./components/diet-chart-editor/add-food-item-menu/add-food-item-menu.component";
import {
    SendDietChartDialogComponent
} from "../modules/care/patients/patient-consultation-v3/components/consultation-diet-chart-editor/send-diet-chart-dialog/send-diet-chart-dialog.component";
import {
    ManageDaysDialogComponent
} from "./components/diet-chart-editor/manage-days-dialog/manage-days-dialog.component";
import { MealTimingInputComponent } from "./components/diet-chart-editor/meal-timing-input/meal-timing-input.component";
import {
    FindAndReplaceFoodItemDialogComponent
} from "./components/diet-chart-editor/find-and-replace-food-item-dialog/find-and-replace-food-item-dialog.component";
import { CdkDrag, CdkDragHandle, CdkDropList } from "@angular/cdk/drag-drop";
import { OverlayModule } from "@angular/cdk/overlay";
import { PortalModule } from "@angular/cdk/portal";
import {
    SaveTemplateDialogComponent
} from "./components/diet-chart-editor/save-template-diaog/save-template-dialog.component";
import { MealBasedDietChartEditorComponent } from './components/diet-chart-editor/meal-based-diet-chart-editor/meal-based-diet-chart-editor.component';
import { MealOptionCardComponent } from './components/diet-chart-editor/meal-option-card/meal-option-card.component';
import { MatStepperModule } from "@angular/material/stepper";
import { EditMealOptionDialogComponent } from './components/diet-chart-editor/edit-meal-option-dialog/edit-meal-option-dialog.component';
import { TagSelectOptionInputComponent } from './components/inputs/tag-select-option-input/tag-select-option-input.component';
import { MarkdownTextComponent } from './components/markdown-text/markdown-text.component';
import { InfoDialogComponent } from './components/info-dialog/info-dialog.component';
import { UserInputComponent } from './components/inputs/user-input/user-input.component';
import { FileUrlInputComponent } from './components/inputs/file-url-input/file-url-input.component';
import { DateTimeInputComponent } from './components/inputs/date-time-input/date-time-input.component';
import { TimeInputComponent } from './components/inputs/time-input/time-input.component';
import { SendBillDialogV3Component } from "./components/send-bill-dialog-v3/send-bill-dialog-v3.component";
import { AutocompleteInputComponent } from './components/inputs/autocomplete-input/autocomplete-input.component';
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { RouterLink } from "@angular/router";
import { ClipboardModule } from "@angular/cdk/clipboard";
import {JsonViewerComponent} from "../modules/standalone/json-viewer/json-viewer.component";
import { UsersInputComponent } from "./components/inputs/users-input/users-input.component";
import { GooglePlaceIdInputComponent } from "./components/inputs/google-place-id-input/google-place-id-input.component";

const MY_DATE_FORMAT = {
    parse: {
        dateInput: 'DD/MM/YYYY', // this is how your date will be parsed from Input
    },
    display: {
        dateInput: 'DD/MM/YYYY', // this is how your date will get displayed on the Input
        monthYearLabel: 'MMMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@NgModule({
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatSnackBarModule,
        MatButtonModule,
        MatInputModule,
        MatFormFieldModule,
        MatAutocompleteModule,
        MatChipsModule,
        MatIconModule,
        MatSelectModule,
        MatListModule,
        MatDialogModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatCheckboxModule,
        QuillModule.forRoot(),
        MatMenuModule,
        MatRippleModule,
        MatTooltipModule,
        CdkDropList,
        CdkDrag,
        CdkDragHandle,
        OverlayModule,
        PortalModule,
        MatStepperModule,
        MatProgressSpinnerModule,
        RouterLink,
        ClipboardModule,
        JsonViewerComponent,
        MarkdownTextComponent
    ],
    declarations: [
        ReverseInfiniteScrollDirective,
        NgVar,
        LinkifyPipe,
        DisplayNamePipe,
        ImageFallbackPipe,
        AddDaysPipe,
        MaintenanceComponent,
        NotFoundComponent,
        TileSelectInputComponent,
        SuccessSnackBarComponent,
        InfoSnackBarComponent,
        FailureSnackBarComponent,
        TagSelectInputComponent,
        SelectOwnerDialogComponent,
        FieldInputDialogComponent,
        BmiPipe,
        IncomingCallComponent,
        PrescribePatientDialogComponent,
        ProgramSelectInputComponent,
        DietChartCardComponent,
        MatQuill,
        FileDragAndDropDirective,
        TdeePipe,
        DayBasedDietChartEditorComponent,
        SaveTemplateDialogComponent,
        MealCardComponent,
        FoodMeasureNamePipe,
        EditMealDialogComponent,
        AddFoodItemMenuComponent,
        SendDietChartDialogComponent,
        ManageDaysDialogComponent,
        MealTimingInputComponent,
        FindAndReplaceFoodItemDialogComponent,
        MealBasedDietChartEditorComponent,
        MealOptionCardComponent,
        SendBillDialogV3Component,
        EditMealOptionDialogComponent,
        TagSelectOptionInputComponent,
        InfoDialogComponent,
        UserInputComponent,
        FileUrlInputComponent,
        DateTimeInputComponent,
        TimeInputComponent,
        AutocompleteInputComponent,
        UsersInputComponent,
        GooglePlaceIdInputComponent
    ],
    exports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ReverseInfiniteScrollDirective,
        NgVar,
        LinkifyPipe,
        DisplayNamePipe,
        ImageFallbackPipe,
        AddDaysPipe,
        TileSelectInputComponent,
        MatSnackBarModule,
        TagSelectInputComponent,
        SelectOwnerDialogComponent,
        BmiPipe,
        TdeePipe,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        ProgramSelectInputComponent,
        PrescribePatientDialogComponent,
        DayBasedDietChartEditorComponent,
        DietChartCardComponent,
        MatQuill,
        FileDragAndDropDirective,
        MatAutocompleteModule,
        MatChipsModule,
        MealBasedDietChartEditorComponent,
        TagSelectOptionInputComponent,
        UserInputComponent,
        FileUrlInputComponent,
        DateTimeInputComponent,
        TimeInputComponent,
        UsersInputComponent
    ],
    providers: [
        MatDatepickerModule,
        {
            provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
            useValue: {
                duration: 2500,
                verticalPosition: 'top',
                horizontalPosition: 'right',
                panelClass: 'custom-snack-bar',
            },
        },
        { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE],
        },
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT },
    ],
})
export class SharedModule {}
